import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, FileEdit, ArrowRight } from 'lucide-react';
import ReporterArticleTable from '../../components/reporter/ReporterArticleTable';
import { fetchMyNews, deleteMyNews } from '../../utils/api/newsApi';
import { useLanguage } from '../../context/LanguageContext';

const content = {
  ne: {
    title: 'मेरा लेखहरू',
    all: 'सबै',
    draft: 'मस्यौदा',
    pending: 'समीक्षामा',
    approved: 'स्वीकृत',
    published: 'प्रकाशित',
    rejected: 'अस्वीकृत',
    createNew: 'नयाँ लेख',
    noArticles: 'कुनै लेख फेला परेन',
  },
  en: {
    title: 'My Articles',
    all: 'All',
    draft: 'Draft',
    pending: 'Pending',
    approved: 'Approved',
    published: 'Published',
    rejected: 'Rejected',
    createNew: 'Create Article',
    noArticles: 'No articles found',
  },
};

const statusFilters = ['all', 'pending', 'published', 'rejected'];

const ReporterArticles = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = content[language] || content.en;
  const [activeFilter, setActiveFilter] = useState('all');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchMyNews()
      .then((res) => {
        setArticles(res.data || []);
      })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeFilter === 'all'
    ? articles
    : articles.filter((a) => a.status === activeFilter);

  const handleDelete = async (article) => {
    const confirmMsg = language === 'ne'
      ? `"${article.title}" मेटाउन चाहनुहुन्छ?`
      : `Delete "${article.title}"?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await deleteMyNews(article._id);
      setArticles((prev) => prev.filter((a) => a._id !== article._id));
    } catch (err) {
      alert(language === 'ne' ? 'मेटाउन असफल भयो' : 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-extrabold text-char-900">{t.title}</h2>
        <button
          onClick={() => navigate('/reporter/create-article')}
          className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-extrabold text-white shadow-green transition-all hover:-translate-y-0.5 hover:bg-primary-500 hover:shadow-green-hover"
        >
          <FileEdit size={16} />
          {t.createNew}
          <ArrowRight size={14} />
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2">
        {statusFilters.map((status) => (
          <button
            key={status}
            onClick={() => setActiveFilter(status)}
            className={`rounded-xl px-4 py-2 text-xs font-extrabold uppercase tracking-wider transition-all ${
              activeFilter === status
                ? 'bg-primary text-white shadow-sm'
                : 'bg-white text-char-500 border border-char-100 hover:border-primary-200 hover:text-primary'
            }`}
          >
            {t[status] || status}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center rounded-3xl border border-char-100 bg-white py-12">
          <p className="text-sm text-char-400">{language === 'ne' ? 'लोड हुँदैछ...' : 'Loading...'}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-char-100 bg-white py-12">
          <FileEdit size={36} className="text-char-300" />
          <p className="mt-3 text-sm text-char-500">{t.noArticles}</p>
        </div>
      ) : (
        <ReporterArticleTable
          articles={filtered}
          language={language}
          onView={(a) => navigate('/reporter/create-article', { state: { article: a, viewOnly: true } })}
          onEdit={(a) => navigate('/reporter/create-article', { state: { article: a } })}
          onResubmit={(a) => navigate('/reporter/create-article', { state: { article: a } })}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default ReporterArticles;
